import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  CreateVoucherCodeRequestBody,
  ListVoucherCodesResponse,
  UpdateVoucherCodeRequestBody,
} from "common";

const VOUCHER_CODE_QUERY_KEY = "/api/admin/voucher-code";

const fetchVoucherCodes = async (
  take: number,
  skip: number
): Promise<ListVoucherCodesResponse> => {
  const res = await axios.get(
    `${VOUCHER_CODE_QUERY_KEY}?take=${take}&skip=${skip}`
  );
  return res.data;
};

export const useGetVoucherCodes = (
  page: number = 0,
  pageSize: number = 10
): UseQueryResult<ListVoucherCodesResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<ListVoucherCodesResponse, Error>({
    queryKey: [VOUCHER_CODE_QUERY_KEY, take, skip],
    queryFn: () => fetchVoucherCodes(take, skip),
    retry: false,
  });
};

export const useCreateVoucherCode = (): UseMutationResult<
  void,
  ErrorNotification,
  CreateVoucherCodeRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, CreateVoucherCodeRequestBody>({
    mutationFn: async (payload: CreateVoucherCodeRequestBody) => {
      const res = await axios.post(VOUCHER_CODE_QUERY_KEY, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "create-voucher-code-success",
        message: "Access Code Created",
        details: "The access code has been created successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [VOUCHER_CODE_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-voucher-code-error",
        message: "Failed to Create Access Code",
        details:
          error?.response?.data?.message ??
          "Failed to create access code. The code may already exist.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateVoucherCode = (): UseMutationResult<
  void,
  ErrorNotification,
  { id: number } & UpdateVoucherCodeRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    void,
    ErrorNotification,
    { id: number } & UpdateVoucherCodeRequestBody
  >({
    mutationFn: async ({ id, ...payload }) => {
      const res = await axios.put(`${VOUCHER_CODE_QUERY_KEY}/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "update-voucher-code-success",
        message: "Access Code Updated",
        details: "The access code has been updated successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [VOUCHER_CODE_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "update-voucher-code-error",
        message: "Failed to Update Access Code",
        details:
          error?.response?.data?.message ?? "Failed to update access code.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteVoucherCode = (): UseMutationResult<
  void,
  ErrorNotification,
  { id: number }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, { id: number }>({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await axios.delete(`${VOUCHER_CODE_QUERY_KEY}/${id}`);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "delete-voucher-code-success",
        message: "Access Code Deleted",
        details: "The access code has been deleted.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [VOUCHER_CODE_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-voucher-code-error",
        message: "Failed to Delete Access Code",
        details:
          error?.response?.data?.message ?? "Failed to delete access code.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
